/**
 * La cabecera de una página ES UNA SOLA FILA. Lo que no entra va a «Más acciones».
 *
 * ── LO QUE PASABA ─────────────────────────────────────────────────────────────────────────────
 * Con cinco o seis botones arriba, Polaris le quitaba ancho al título: el estado («Sin pago») y
 * los íconos del cliente se caían a una segunda fila debajo de L-00012. El founder (20-sep-2026):
 * «cuando se cargan más botones en el header se agrupan en dos filas y eso está mal… debe mostrar
 * solo las opciones importantes y el resto en Más acciones… estricto para todas las páginas».
 *
 * ⭐ ES LO QUE HACE SHOPIFY ADMIN: el título nunca cede; los botones secundarios se pliegan en un
 *    solo menú, en orden de importancia (el primero de la lista es el más importante y el último
 *    en plegarse). El botón primario no se pliega nunca.
 *
 * ⭐ UNA SOLA PUERTA PARA 160 PÁGINAS. Este `Page` reemplaza al de Polaris en el barrel
 *    `@/lib/polaris`, así que toda página —use `Page`, `AppPage` o `MarcoDeVenta`— hereda la regla
 *    sin tocarla. Es la única forma de que «estricto para todas» sea verdad la semana que viene.
 *
 * 🪤 POR QUÉ NO ALCANZA CON EL PLEGADO DE POLARIS. Polaris ya mide y esconde lo que no entra, pero
 *    (1) mide DESPUÉS de que el título cedió ancho —por eso se veían dos filas— y (2) cuando la
 *    página ya trae su propio grupo «Más acciones», crea OTRO botón «Más acciones» para lo que
 *    escondió: dos menús con el mismo nombre. Acá se decide antes: se mide lo que hay, se calcula
 *    cuántos entran, y lo que sobra se funde en el grupo que ya existe (o se crea uno). Polaris
 *    recibe sólo lo que cabe y no tiene nada que esconder. El CSS que impide que el título ceda
 *    está en `styles/polaris.css` («una sola fila»).
 *
 * 🪤 EN MÓVIL NO SE TOCA NADA: ahí Polaris apila título y acciones a propósito (y pliega en «⋯»).
 */
import { useLayoutEffect, useRef, useState } from 'react';
import {
  Page as PaginaDePolaris, Button,
  type PageProps, type MenuActionDescriptor, type MenuGroupDescriptor, type ActionListItemDescriptor,
} from '@shopify/polaris';

/** Separación entre botones en la fila de acciones de Polaris (`--p-space-200`). */
const SEPARACION = 8;
/** Lo que se deja libre para que la medición de Polaris nunca tenga que esconder nada por su cuenta. */
const MARGEN = 12;
const ESCRITORIO = '(min-width: 48em)';

/**
 * Cuántas acciones sueltas entran, en orden, en el ancho disponible — la regla pura.
 *
 * Si algo queda afuera (o la página ya trae grupos) hace falta el botón del menú, y ese ancho
 * también se descuenta. Devuelve `n` cuando entran todas.
 */
export function cuantasEntran(m: {
  disponible: number;
  anchos: number[];
  anchoDelMenu: number;
  hayGrupos: boolean;
}): number {
  const n = m.anchos.length;
  const cabe = (k: number) => {
    const conMenu = m.hayGrupos || k < n;
    const piezas = k + (conMenu ? 1 : 0);
    const suma = m.anchos.slice(0, k).reduce((a, b) => a + b, 0) + (conMenu ? m.anchoDelMenu : 0);
    return suma + Math.max(0, piezas - 1) * SEPARACION <= m.disponible;
  };
  let k = n;
  while (k > 0 && !cabe(k)) k -= 1;
  return k;
}

/** Las que no entraron se funden en el primer grupo (o en uno nuevo, «Más acciones»), primero ellas. */
export function repartir(
  sueltas: MenuActionDescriptor[],
  grupos: MenuGroupDescriptor[],
  k: number,
): { secondaryActions: MenuActionDescriptor[]; actionGroups: MenuGroupDescriptor[] | undefined } {
  if (k >= sueltas.length) return { secondaryActions: sueltas, actionGroups: grupos.length ? grupos : undefined };
  const ocultas: ActionListItemDescriptor[] = sueltas.slice(k).map(({ content, onAction, icon, disabled, destructive, url, external, accessibilityLabel }) => ({
    content, onAction, icon, disabled, destructive, url, external, accessibilityLabel,
  }));
  const [primero, ...resto] = grupos;
  const fundido: MenuGroupDescriptor = primero
    ? { ...primero, actions: [...ocultas, ...primero.actions] }
    : { title: 'Más acciones', actions: ocultas };
  return { secondaryActions: sueltas.slice(0, k), actionGroups: [fundido, ...resto] };
}

const esLista = (v: unknown): v is MenuActionDescriptor[] => Array.isArray(v);

/*
 * 🪤 LAS PROPS VAN LAXAS A PROPÓSITO. `packages/shared/src/types/polaris-compat.d.ts` declara el
 *    `Page` de Polaris como `(props: any)` porque el repo le pasa títulos en JSX y acciones con
 *    íconos de otro tipo; si este reemplazo tipara estricto, 125 páginas dejarían de compilar sin
 *    haber cambiado. Se acepta lo mismo que antes y se le pasa a Polaris tal cual.
 */
type PropsDeLaPagina = Omit<PageProps, 'title' | 'primaryAction' | 'backAction' | 'secondaryActions'> & {
  title?: unknown;
  primaryAction?: unknown;
  backAction?: unknown;
  secondaryActions?: unknown;
};

export function PaginaDeUnaFila(props: PropsDeLaPagina) {
  const sueltas = esLista(props.secondaryActions) ? props.secondaryActions : null;
  const grupos = props.actionGroups ?? [];
  const raiz = useRef<HTMLDivElement>(null);
  const medidor = useRef<HTMLDivElement>(null);
  /** Cuántas sueltas se muestran; `null` = todas. */
  const [cuantas, setCuantas] = useState<number | null>(null);

  // Lo que cambia el ancho de los botones: sus textos, sus íconos, y el título del grupo.
  const firma = (sueltas ?? []).map((a) => `${a.content}|${a.icon ? 'i' : ''}`).join('·')
    + '§' + grupos.map((g) => g.title).join('·');

  useLayoutEffect(() => {
    const el = raiz.current;
    if (!el || !sueltas || sueltas.length === 0) { setCuantas(null); return; }

    const medir = () => {
      if (!window.matchMedia(ESCRITORIO).matches) { setCuantas(null); return; }
      const fila = el.querySelector<HTMLElement>('.Polaris-Page-Header__Row');
      const titulo = el.querySelector<HTMLElement>('.Polaris-Page-Header__TitleWrapper');
      const primario = el.querySelector<HTMLElement>('.Polaris-Page-Header__PrimaryActionWrapper');
      const botones = medidor.current ? Array.from(medidor.current.children) as HTMLElement[] : [];
      if (!fila || !titulo || botones.length < sueltas.length + 1) return;
      const anchos = botones.slice(0, sueltas.length).map((b) => b.offsetWidth);
      const anchoDelMenu = botones[sueltas.length].offsetWidth;
      // Si todavía no hay medidas (fuentes, primer pintado) no se decide nada.
      if (anchos.some((a) => a === 0) || anchoDelMenu === 0) return;
      const disponible = fila.getBoundingClientRect().right - titulo.getBoundingClientRect().right
        - 16 /* margen izquierdo de la zona de acciones */
        - (primario ? primario.offsetWidth + 4 : 0)
        - MARGEN;
      const k = cuantasEntran({ disponible, anchos, anchoDelMenu, hayGrupos: grupos.length > 0 });
      setCuantas((prev) => { const nuevo = k >= sueltas.length ? null : k; return prev === nuevo ? prev : nuevo; });
    };

    medir();
    // Se vuelve a medir sólo cuando cambia el ANCHO de la página (no cada vez que crece el contenido).
    let anchoAnterior = el.getBoundingClientRect().width;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver((entradas) => {
      const ancho = entradas[0]?.contentRect.width ?? el.getBoundingClientRect().width;
      if (ancho === anchoAnterior) return;
      anchoAnterior = ancho;
      medir();
    }) : null;
    ro?.observe(el);
    // Las fuentes pueden llegar después del primer pintado y cambiar el ancho de cada botón.
    void document.fonts?.ready.then(() => medir());
    return () => ro?.disconnect();
    // `firma` resume todo lo que cambia el ancho de los botones; `sueltas`/`grupos` cambian de identidad en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, !!props.primaryAction, !!props.backAction]);

  const dePolaris = props as PageProps;
  if (!sueltas) return <PaginaDePolaris {...dePolaris} />;

  const repartidas = repartir(sueltas, grupos, cuantas ?? sueltas.length);
  return (
    <div ref={raiz}>
      <PaginaDePolaris {...dePolaris} secondaryActions={repartidas.secondaryActions} actionGroups={repartidas.actionGroups} />
      {/* Los mismos botones, invisibles, para medir cuánto ocupa cada uno. El último es el del menú. */}
      <div ref={medidor} aria-hidden style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', display: 'flex', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
        {sueltas.map((a, i) => <Button key={i} icon={a.icon}>{a.content}</Button>)}
        <Button disclosure>{grupos[0]?.title ?? 'Más acciones'}</Button>
      </div>
    </div>
  );
}

